'use client';

import React from 'react';
import { useRouter } from '@/lib/router';
import { affiliateData, referrals, commissions, withdrawals, formatRupiah } from '@/lib/mock-data';
import { StatsCard, StatusBadge, PageHeader, DataTable, SearchInput, TabFilter } from '@/components/shared';

// ============================================
// 1. AFFILIATE DASHBOARD PAGE
// ============================================

export function AffiliateDashboardPage() {
  const navigate = useRouter((s) => s.navigate);

  // Copy / toast state
  const [toast, setToast] = React.useState<string | null>(null);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2500);
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text).then(() => {
      showToast(`${label} berhasil disalin!`);
    });
  };

  const recentReferrals = referrals.slice(0, 5);
  const recentCommissions = commissions.slice(0, 5);

  return (
    <div className="space-y-6">
      {/* Toast notification */}
      {toast && (
        <div className="fixed top-6 right-6 z-50 bg-emerald-600 text-white text-sm font-medium px-4 py-3 rounded-xl shadow-lg shadow-emerald-600/25 animate-[fadeIn_0.2s_ease-out]">
          ✓ {toast}
        </div>
      )}

      {/* Welcome Section */}
      <div className="bg-gradient-to-r from-orange-600/20 via-amber-600/10 to-transparent border border-orange-500/20 rounded-2xl p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-50 mb-1">
              Halo, Sipod! 👋
            </h1>
            <p className="text-sm text-slate-400">
              Kamu sudah membantu <span className="text-amber-400 font-semibold">{affiliateData.stats.total_referrals} bisnis</span> tumbuh.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/affiliate/referrals')}
              className="px-4 py-2 text-sm font-medium bg-slate-800 border border-slate-700 rounded-lg text-slate-300 hover:text-white hover:border-slate-600 transition-all"
            >
              Lihat Referral →
            </button>
            <button
              onClick={() => navigate('/affiliate/withdrawals')}
              className="px-4 py-2 text-sm font-medium bg-orange-600 hover:bg-orange-700 text-white rounded-lg transition-all"
            >
              Tarik Dana
            </button>
          </div>
        </div>
      </div>

      {/* Referral Code Widget */}
      <div className="bg-slate-800 border border-slate-700 rounded-2xl p-6">
        <div className="flex items-center gap-2 mb-4">
          <span className="text-lg">🔗</span>
          <h2 className="text-lg font-semibold text-slate-50">Kode & Link Referral</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
          {/* Referral Code */}
          <div className="bg-slate-900 rounded-xl p-4 border border-slate-700">
            <p className="text-xs text-slate-500 uppercase tracking-wider mb-2 font-medium">Kode Referral</p>
            <div className="flex items-center gap-3">
              <code className="flex-1 text-xl font-bold text-amber-400 bg-slate-800 rounded-lg px-4 py-2.5 tracking-wider border border-slate-700">
                {affiliateData.code}
              </code>
              <button
                onClick={() => copyToClipboard(affiliateData.code, 'Kode referral')}
                className="p-2.5 rounded-lg bg-slate-800 border border-slate-700 text-slate-400 hover:text-orange-400 hover:border-orange-500/50 transition-all"
                title="Salin kode"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
              </button>
            </div>
          </div>

          {/* Referral Link */}
          <div className="bg-slate-900 rounded-xl p-4 border border-slate-700">
            <p className="text-xs text-slate-500 uppercase tracking-wider mb-2 font-medium">Link Referral</p>
            <div className="flex items-center gap-3">
              <p className="flex-1 text-sm text-slate-300 bg-slate-800 rounded-lg px-4 py-2.5 truncate border border-slate-700">
                {affiliateData.referral_link}
              </p>
              <button
                onClick={() => copyToClipboard(affiliateData.referral_link, 'Link referral')}
                className="p-2.5 rounded-lg bg-slate-800 border border-slate-700 text-slate-400 hover:text-orange-400 hover:border-orange-500/50 transition-all"
                title="Salin link"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
              </button>
            </div>
          </div>
        </div>

        {/* Share Buttons */}
        <div className="flex items-center gap-3">
          <span className="text-sm text-slate-500">Bagikan:</span>
          <button
            onClick={() => {
              const text = `Yuk pakai Rekanku! AI Agent untuk bisnis UMKM. Daftar pakai kode ${affiliateData.code} dan dapatkan keuntungan! 🔥 ${affiliateData.referral_link}`;
              const waUrl = `https://wa.me/?text=${encodeURIComponent(text)}`;
              window.open(waUrl, '_blank');
            }}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600/20 border border-emerald-600/30 text-emerald-400 text-sm font-medium hover:bg-emerald-600/30 transition-all"
          >
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
            WhatsApp
          </button>
          <button
            onClick={() => {
              const text = `Yuk pakai Rekanku! AI Agent untuk bisnis UMKM. Daftar pakai kode ${affiliateData.code} dan dapatkan keuntungan! 🔥 ${affiliateData.referral_link}`;
              const tgUrl = `https://t.me/share/url?url=${encodeURIComponent(affiliateData.referral_link)}&text=${encodeURIComponent(text)}`;
              window.open(tgUrl, '_blank');
            }}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-sky-600/20 border border-sky-600/30 text-sky-400 text-sm font-medium hover:bg-sky-600/30 transition-all"
          >
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M11.944 0A12 12 0 000 12a12 12 0 0012 12 12 12 0 0012-12A12 12 0 0012 0a12 12 0 00-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 01.171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.479.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/></svg>
            Telegram
          </button>
          <button
            onClick={() => copyToClipboard(affiliateData.referral_link, 'Link referral')}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-700/50 border border-slate-600 text-slate-300 text-sm font-medium hover:bg-slate-700 hover:text-white transition-all"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
            Salin Link
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatsCard
          title="Saldo Tersedia"
          value={formatRupiah(affiliateData.stats.available_balance)}
          icon={
            <svg className="w-6 h-6 text-orange-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" /></svg>
          }
          iconBg="bg-orange-500/20"
          iconColor="text-orange-400"
          onClick={() => navigate('/affiliate/withdrawals')}
        />
        <StatsCard
          title="Total Komisi"
          value={formatRupiah(affiliateData.stats.total_commission_earned)}
          icon={
            <svg className="w-6 h-6 text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
          }
          iconBg="bg-amber-500/20"
          iconColor="text-amber-400"
          onClick={() => navigate('/affiliate/commissions')}
        />
        <StatsCard
          title="Pending Komisi"
          value={formatRupiah(affiliateData.stats.pending_commission)}
          icon={
            <svg className="w-6 h-6 text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
          }
          iconBg="bg-yellow-500/20"
          iconColor="text-yellow-400"
        />
      </div>

      {/* Two-column: Recent Referrals & Recent Commissions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Referrals */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-slate-50">Referral Terbaru</h2>
            <button
              onClick={() => navigate('/affiliate/referrals')}
              className="text-sm text-orange-400 hover:text-orange-300 font-medium transition-colors"
            >
              Lihat Semua →
            </button>
          </div>
          <DataTable
            columns={[
              { key: 'name', label: 'Nama Bisnis', className: 'min-w-[140px]' },
              { key: 'email', label: 'Email', className: 'hidden sm:table-cell min-w-[160px]' },
              { key: 'registered', label: 'Tgl Daftar', className: 'hidden md:table-cell min-w-[100px]' },
              { key: 'status', label: 'Status', render: (item: typeof referrals[0]) => <StatusBadge status={item.status} /> },
            ]}
            data={recentReferrals}
          />
        </div>

        {/* Recent Commissions */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-slate-50">Komisi Terbaru</h2>
            <button
              onClick={() => navigate('/affiliate/commissions')}
              className="text-sm text-orange-400 hover:text-orange-300 font-medium transition-colors"
            >
              Lihat Semua →
            </button>
          </div>
          <DataTable
            columns={[
              { key: 'referral', label: 'Referral', className: 'min-w-[140px]' },
              { key: 'amount', label: 'Jumlah', render: (item: typeof commissions[0]) => <span className="text-amber-400 font-semibold">{formatRupiah(item.amount)}</span> },
              { key: 'status', label: 'Status', render: (item: typeof commissions[0]) => <StatusBadge status={item.status} /> },
              { key: 'date', label: 'Tanggal', className: 'hidden sm:table-cell min-w-[100px]' },
            ]}
            data={recentCommissions}
          />
        </div>
      </div>
    </div>
  );
}

// ============================================
// 2. AFFILIATE REFERRALS PAGE
// ============================================

export function AffiliateReferralsPage() {
  const [search, setSearch] = React.useState('');
  const [activeTab, setActiveTab] = React.useState('all');
  const [toast, setToast] = React.useState<string | null>(null);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2500);
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text).then(() => {
      showToast(`${label} berhasil disalin!`);
    });
  };

  const tabs = [
    { label: 'Semua', value: 'all', count: referrals.length },
    { label: 'Aktif', value: 'active', count: referrals.filter(r => r.status === 'active').length },
    { label: 'Trial', value: 'trial', count: referrals.filter(r => r.status === 'trial').length },
    { label: 'Churn', value: 'expired', count: referrals.filter(r => r.status === 'expired').length },
  ];

  const filteredReferrals = React.useMemo(() => {
    let data = referrals;
    if (activeTab !== 'all') {
      data = data.filter(r => r.status === activeTab);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      data = data.filter(r =>
        r.name.toLowerCase().includes(q) ||
        r.email.toLowerCase().includes(q)
      );
    }
    return data;
  }, [search, activeTab]);

  return (
    <div className="space-y-6">
      {/* Toast */}
      {toast && (
        <div className="fixed top-6 right-6 z-50 bg-emerald-600 text-white text-sm font-medium px-4 py-3 rounded-xl shadow-lg shadow-emerald-600/25">
          ✓ {toast}
        </div>
      )}

      <PageHeader
        title="Referral Saya"
        subtitle={`${affiliateData.stats.total_referrals} total · ${affiliateData.stats.active_referrals} aktif`}
      />

      {/* Copy Referral Code Section */}
      <div className="bg-slate-800 border border-slate-700 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <span className="text-lg">🔗</span>
          <div className="min-w-0 flex-1">
            <p className="text-xs text-slate-500 uppercase tracking-wider font-medium mb-1">Kode Referral Kamu</p>
            <div className="flex items-center gap-3">
              <code className="text-lg font-bold text-amber-400 tracking-wider">{affiliateData.code}</code>
              <span className="text-xs text-slate-500 hidden sm:inline truncate">— {affiliateData.referral_link}</span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => copyToClipboard(affiliateData.code, 'Kode referral')}
            className="px-4 py-2 text-sm font-medium bg-slate-700 border border-slate-600 rounded-lg text-slate-300 hover:text-white hover:border-slate-500 transition-all"
          >
            Salin Kode
          </button>
          <button
            onClick={() => copyToClipboard(affiliateData.referral_link, 'Link referral')}
            className="px-4 py-2 text-sm font-medium bg-orange-600 hover:bg-orange-700 text-white rounded-lg transition-all"
          >
            Salin Link
          </button>
        </div>
      </div>

      {/* Search + Tabs */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div className="w-full sm:w-72">
          <SearchInput
            placeholder="Cari nama bisnis atau email..."
            value={search}
            onChange={setSearch}
          />
        </div>
        <TabFilter tabs={tabs} active={activeTab} onChange={setActiveTab} />
      </div>

      {/* Table */}
      <DataTable
        columns={[
          {
            key: 'name',
            label: 'Nama Bisnis',
            className: 'min-w-[160px]',
            render: (item: typeof referrals[0]) => (
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-orange-500/20 flex items-center justify-center text-orange-400 text-xs font-bold flex-shrink-0">
                  {item.name.split(' ').map(w => w[0]).slice(0, 2).join('')}
                </div>
                <span className="font-medium text-slate-100">{item.name}</span>
              </div>
            ),
          },
          {
            key: 'email',
            label: 'Email',
            className: 'hidden sm:table-cell min-w-[170px]',
            render: (item: typeof referrals[0]) => <span className="text-slate-400">{item.email}</span>,
          },
          { key: 'registered', label: 'Tgl Daftar', className: 'hidden md:table-cell min-w-[110px]' },
          { key: 'status', label: 'Status', render: (item: typeof referrals[0]) => <StatusBadge status={item.status} /> },
          {
            key: 'commission',
            label: 'Komisi',
            className: 'text-right min-w-[100px]',
            render: (item: typeof referrals[0]) => (
              <span className="text-amber-400 font-semibold">{item.commission > 0 ? formatRupiah(item.commission) : '-'}</span>
            ),
          },
        ]}
        data={filteredReferrals}
      />
    </div>
  );
}

// ============================================
// 3. AFFILIATE COMMISSIONS PAGE
// ============================================

export function AffiliateCommissionsPage() {
  const [activeTab, setActiveTab] = React.useState('all');

  const tabs = [
    { label: 'Semua', value: 'all', count: commissions.length },
    { label: 'Pending', value: 'pending', count: commissions.filter(c => c.status === 'pending').length },
    { label: 'Approved', value: 'approved', count: commissions.filter(c => c.status === 'approved').length },
    { label: 'Paid', value: 'paid', count: commissions.filter(c => c.status === 'paid').length },
    { label: 'Rejected', value: 'rejected', count: commissions.filter(c => c.status === 'rejected').length },
  ];

  const filteredCommissions = React.useMemo(() => {
    if (activeTab === 'all') return commissions;
    return commissions.filter(c => c.status === activeTab);
  }, [activeTab]);

  const totalEarned = commissions.filter(c => c.status === 'paid').reduce((sum, c) => sum + c.amount, 0);
  const totalPending = commissions.filter(c => c.status === 'pending' || c.status === 'approved').reduce((sum, c) => sum + c.amount, 0);
  const totalPaid = commissions.filter(c => c.status === 'paid').reduce((sum, c) => sum + c.amount, 0);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Komisi"
        subtitle="Riwayat komisi referral kamu"
      />

      {/* Summary Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatsCard
          title="Total Earned"
          value={formatRupiah(totalEarned + totalPending)}
          icon={
            <svg className="w-6 h-6 text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" /></svg>
          }
          iconBg="bg-amber-500/20"
          iconColor="text-amber-400"
        />
        <StatsCard
          title="Pending"
          value={formatRupiah(totalPending)}
          icon={
            <svg className="w-6 h-6 text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
          }
          iconBg="bg-yellow-500/20"
          iconColor="text-yellow-400"
        />
        <StatsCard
          title="Paid"
          value={formatRupiah(totalPaid)}
          icon={
            <svg className="w-6 h-6 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
          }
          iconBg="bg-emerald-500/20"
          iconColor="text-emerald-400"
        />
      </div>

      {/* Tabs */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <TabFilter tabs={tabs} active={activeTab} onChange={setActiveTab} />
        <p className="text-sm text-slate-500">{filteredCommissions.length} komisi ditampilkan</p>
      </div>

      {/* Table */}
      <DataTable
        columns={[
          {
            key: 'referral',
            label: 'Referral',
            className: 'min-w-[160px]',
            render: (item: typeof commissions[0]) => (
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-amber-500/20 flex items-center justify-center text-amber-400 text-xs font-bold flex-shrink-0">
                  {item.referral.split(' ').map(w => w[0]).slice(0, 2).join('')}
                </div>
                <span className="font-medium text-slate-100">{item.referral}</span>
              </div>
            ),
          },
          {
            key: 'amount',
            label: 'Jumlah',
            className: 'min-w-[120px]',
            render: (item: typeof commissions[0]) => (
              <span className="text-amber-400 font-semibold">{formatRupiah(item.amount)}</span>
            ),
          },
          {
            key: 'status',
            label: 'Status',
            render: (item: typeof commissions[0]) => <StatusBadge status={item.status} />,
          },
          { key: 'date', label: 'Tanggal', className: 'hidden sm:table-cell min-w-[110px]' },
          {
            key: 'invoice',
            label: 'Invoice',
            className: 'hidden md:table-cell min-w-[120px]',
            render: (item: typeof commissions[0]) => (
              <span className={`text-xs font-mono ${item.invoice === '-' ? 'text-slate-600' : 'text-slate-400'}`}>
                {item.invoice}
              </span>
            ),
          },
        ]}
        data={filteredCommissions}
      />
    </div>
  );
}

// ============================================
// 4. AFFILIATE WITHDRAWALS PAGE
// ============================================

export function AffiliateWithdrawalsPage() {
  const navigate = useRouter((s) => s.navigate);
  const [toast, setToast] = React.useState<string | null>(null);
  const [showSuccess, setShowSuccess] = React.useState(false);

  // Form state
  const [amount, setAmount] = React.useState('');
  const [paymentMethod, setPaymentMethod] = React.useState('bank_transfer');
  const [bankName, setBankName] = React.useState('BCA');
  const [accountNumber, setAccountNumber] = React.useState('');
  const [accountHolder, setAccountHolder] = React.useState('');

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2500);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!amount || !accountNumber || !accountHolder) {
      showToast('Mohon lengkapi semua field yang diperlukan');
      return;
    }
    const amountNum = parseInt(amount.replace(/\D/g, ''), 10);
    if (amountNum < 100000) {
      showToast('Minimum penarikan Rp 100.000');
      return;
    }
    if (amountNum > affiliateData.stats.available_balance) {
      showToast('Saldo tidak mencukupi');
      return;
    }
    setShowSuccess(true);
    setTimeout(() => {
      setShowSuccess(false);
      setAmount('');
      setAccountNumber('');
      setAccountHolder('');
    }, 3000);
  };

  const formatAmountDisplay = (val: string) => {
    const num = val.replace(/\D/g, '');
    if (!num) return '';
    return new Intl.NumberFormat('id-ID').format(parseInt(num, 10));
  };

  const handleAmountChange = (val: string) => {
    const num = val.replace(/\D/g, '');
    setAmount(num);
  };

  return (
    <div className="space-y-6">
      {/* Toast */}
      {toast && (
        <div className="fixed top-6 right-6 z-50 bg-emerald-600 text-white text-sm font-medium px-4 py-3 rounded-xl shadow-lg shadow-emerald-600/25">
          ✓ {toast}
        </div>
      )}

      <PageHeader
        title="Penarikan Dana"
        subtitle="Tarik komisi ke rekening bank kamu"
      />

      {/* Success Modal */}
      {showSuccess && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="bg-slate-800 border border-slate-700 rounded-2xl p-8 max-w-sm w-full mx-4 text-center shadow-2xl">
            <div className="w-16 h-16 rounded-full bg-emerald-500/20 flex items-center justify-center text-3xl mx-auto mb-4">✅</div>
            <h3 className="text-xl font-bold text-slate-50 mb-2">Berhasil Diajukan!</h3>
            <p className="text-sm text-slate-400 mb-6">
              Penarikan dana {formatRupiah(parseInt(amount, 10))} sedang diproses. Estimasi 1-3 hari kerja.
            </p>
            <button
              onClick={() => setShowSuccess(false)}
              className="w-full py-2.5 bg-orange-600 hover:bg-orange-700 text-white rounded-lg text-sm font-semibold transition-all"
            >
              Mengerti
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Column - Balance + Form */}
        <div className="space-y-6">
          {/* Current Balance Card */}
          <div className="bg-gradient-to-br from-orange-600/20 via-amber-600/10 to-transparent border border-orange-500/20 rounded-2xl p-6">
            <p className="text-sm text-slate-400 mb-2">Saldo Tersedia</p>
            <p className="text-3xl font-bold text-amber-400 mb-1">{formatRupiah(affiliateData.stats.available_balance)}</p>
            <p className="text-xs text-slate-500">Minimum penarikan: {formatRupiah(100000)}</p>
          </div>

          {/* Withdrawal Form */}
          <div className="bg-slate-800 border border-slate-700 rounded-2xl p-6">
            <h2 className="text-lg font-semibold text-slate-50 mb-5">Form Penarikan</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Amount */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1.5">
                  Jumlah Penarikan (Rupiah)
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 text-sm font-medium">Rp</span>
                  <input
                    type="text"
                    inputMode="numeric"
                    placeholder="0"
                    value={formatAmountDisplay(amount)}
                    onChange={(e) => handleAmountChange(e.target.value)}
                    className="w-full bg-slate-900 border border-slate-700 rounded-xl pl-11 pr-20 py-3 text-lg text-slate-100 placeholder:text-slate-600 focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500/50 transition-all"
                  />
                  {amount && parseInt(amount) >= 100000 && (
                    <button
                      type="button"
                      onClick={() => setAmount(String(affiliateData.stats.available_balance))}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-orange-400 hover:text-orange-300 font-medium bg-orange-500/10 px-2 py-1 rounded-md transition-colors"
                    >
                      Max
                    </button>
                  )}
                </div>
              </div>

              {/* Payment Method */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1.5">
                  Metode Pembayaran
                </label>
                <select
                  value={paymentMethod}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                  className="w-full bg-slate-900 border border-slate-700 rounded-xl px-4 py-3 text-sm text-slate-100 focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500/50 transition-all appearance-none cursor-pointer"
                >
                  <option value="bank_transfer">Bank Transfer</option>
                </select>
              </div>

              {/* Bank Name */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1.5">
                  Nama Bank
                </label>
                <select
                  value={bankName}
                  onChange={(e) => setBankName(e.target.value)}
                  className="w-full bg-slate-900 border border-slate-700 rounded-xl px-4 py-3 text-sm text-slate-100 focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500/50 transition-all appearance-none cursor-pointer"
                >
                  <option value="BCA">Bank BCA</option>
                  <option value="Mandiri">Bank Mandiri</option>
                  <option value="BNI">Bank BNI</option>
                  <option value="BRI">Bank BRI</option>
                  <option value="BSI">Bank BSI</option>
                  <option value="CIMB">CIMB Niaga</option>
                  <option value="Danamon">Bank Danamon</option>
                  <option value="Permata">Bank Permata</option>
                </select>
              </div>

              {/* Account Number */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1.5">
                  Nomor Rekening
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  placeholder="Masukkan nomor rekening"
                  value={accountNumber}
                  onChange={(e) => setAccountNumber(e.target.value.replace(/\D/g, ''))}
                  className="w-full bg-slate-900 border border-slate-700 rounded-xl px-4 py-3 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500/50 transition-all"
                />
              </div>

              {/* Account Holder */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1.5">
                  Nama Pemilik Rekening
                </label>
                <input
                  type="text"
                  placeholder="Sesuai nama di buku rekening"
                  value={accountHolder}
                  onChange={(e) => setAccountHolder(e.target.value)}
                  className="w-full bg-slate-900 border border-slate-700 rounded-xl px-4 py-3 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500/50 transition-all"
                />
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                className="w-full py-3 bg-orange-600 hover:bg-orange-700 text-white text-sm font-semibold rounded-xl transition-all hover:shadow-lg hover:shadow-orange-600/20 mt-2"
              >
                Ajukan Penarikan
              </button>
            </form>
          </div>
        </div>

        {/* Right Column - History + Status Flow */}
        <div className="space-y-6">
          {/* Status Flow Explanation */}
          <div className="bg-slate-800 border border-slate-700 rounded-2xl p-6">
            <h2 className="text-lg font-semibold text-slate-50 mb-4">Alur Penarikan</h2>
            <div className="flex items-center gap-3">
              {/* Step 1 - Pending */}
              <div className="flex-1 text-center">
                <div className="w-12 h-12 rounded-full bg-amber-500/20 border-2 border-amber-500/50 flex items-center justify-center mx-auto mb-2">
                  <span className="text-lg">📋</span>
                </div>
                <p className="text-xs font-semibold text-amber-400">Pending</p>
                <p className="text-[10px] text-slate-500 mt-1">Diajukan</p>
              </div>
              {/* Arrow */}
              <div className="text-slate-600 flex-shrink-0">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
              </div>
              {/* Step 2 - Approved */}
              <div className="flex-1 text-center">
                <div className="w-12 h-12 rounded-full bg-blue-500/20 border-2 border-blue-500/50 flex items-center justify-center mx-auto mb-2">
                  <span className="text-lg">✅</span>
                </div>
                <p className="text-xs font-semibold text-blue-400">Approved</p>
                <p className="text-[10px] text-slate-500 mt-1">Diverifikasi</p>
              </div>
              {/* Arrow */}
              <div className="text-slate-600 flex-shrink-0">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
              </div>
              {/* Step 3 - Paid */}
              <div className="flex-1 text-center">
                <div className="w-12 h-12 rounded-full bg-emerald-500/20 border-2 border-emerald-500/50 flex items-center justify-center mx-auto mb-2">
                  <span className="text-lg">💰</span>
                </div>
                <p className="text-xs font-semibold text-emerald-400">Paid</p>
                <p className="text-[10px] text-slate-500 mt-1">Dana dikirim</p>
              </div>
            </div>
            <p className="text-xs text-slate-500 mt-4 text-center">
              Proses verifikasi 1-3 hari kerja. Dana ditransfer ke rekening terdaftar.
            </p>
          </div>

          {/* Withdrawal History */}
          <div>
            <h2 className="text-lg font-semibold text-slate-50 mb-4">Riwayat Penarikan</h2>
            <DataTable
              columns={[
                {
                  key: 'id',
                  label: 'ID',
                  className: 'min-w-[70px]',
                  render: (item: typeof withdrawals[0]) => (
                    <span className="text-xs font-mono text-slate-400">{item.id}</span>
                  ),
                },
                {
                  key: 'amount',
                  label: 'Jumlah',
                  className: 'min-w-[110px]',
                  render: (item: typeof withdrawals[0]) => (
                    <span className="text-amber-400 font-semibold">{formatRupiah(item.amount)}</span>
                  ),
                },
                {
                  key: 'method',
                  label: 'Metode',
                  className: 'hidden sm:table-cell min-w-[80px]',
                  render: (item: typeof withdrawals[0]) => (
                    <span className="text-slate-400 capitalize">{item.method.replace('_', ' ')}</span>
                  ),
                },
                {
                  key: 'bank',
                  label: 'Bank',
                  className: 'hidden md:table-cell min-w-[60px]',
                },
                {
                  key: 'status',
                  label: 'Status',
                  render: (item: typeof withdrawals[0]) => <StatusBadge status={item.status} />,
                },
                {
                  key: 'date',
                  label: 'Tanggal',
                  className: 'hidden lg:table-cell min-w-[100px]',
                },
              ]}
              data={withdrawals}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
